import React, { useEffect, useState } from 'react';
import MainLayout from '../../layout/MainLayout';
import { api } from '../../api/axios';
import { tableDateFormate } from '../../utils/dateHelper';
import PaginationBar from '../../components/PaginationBar';
import { Bug, X } from 'lucide-react';

const PAGE_SIZE = 20;

export default function CrashReports() {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [platformFilter, setPlatformFilter] = useState('');
    const [fatalOnly, setFatalOnly] = useState(false);
    const [selected, setSelected] = useState(null);

    const fetchItems = async (p = page) => {
        setLoading(true);
        setNotFound(false);
        try {
            const params = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
            if (platformFilter) params.set('platform', platformFilter);
            if (fatalOnly) params.set('isFatal', 'true');
            const res = await api.get(`/crash-reports?${params.toString()}`);
            const data = res.data || {};
            setItems(data.items || []);
            setTotalItems(data.total ?? 0);
            setTotalPages(data.totalPages ?? 1);
        } catch (err) {
            if (err?.response?.status === 404) {
                setNotFound(true);
                setItems([]);
            } else {
                console.error('Crash-reports fetch error:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, platformFilter, fatalOnly]);

    return (
        <MainLayout title="Crash Reports">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Platform:</label>
                    <select
                        value={platformFilter}
                        onChange={e => { setPlatformFilter(e.target.value); setPage(1); }}
                        className="border rounded px-3 py-1.5 text-sm"
                    >
                        <option value="">All</option>
                        <option value="android">Android</option>
                        <option value="ios">iOS</option>
                    </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={fatalOnly}
                        onChange={e => { setFatalOnly(e.target.checked); setPage(1); }}
                    />
                    Fatal only
                </label>
                <div className="ml-auto text-sm text-gray-500">{totalItems} total</div>
            </div>

            <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-4 py-3">When</th>
                            <th className="px-4 py-3">Message</th>
                            <th className="px-4 py-3">Platform</th>
                            <th className="px-4 py-3">App ver</th>
                            <th className="px-4 py-3">User</th>
                            <th className="px-4 py-3">Fatal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-gray-400">Loading...</td></tr>
                        ) : notFound ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center text-gray-400">
                                    <div className="flex flex-col items-center gap-2">
                                        <Bug size={32} className="text-gray-300" />
                                        <div className="font-medium">Crash reports API not available</div>
                                        <div className="text-xs">Deploy <code>GET /api/v1/crash-reports</code> on the server.</div>
                                    </div>
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-gray-400">No crash reports. 🎉</td></tr>
                        ) : (
                            items.map(it => (
                                <tr
                                    key={it._id}
                                    className="border-b hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelected(it)}
                                >
                                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                        {tableDateFormate(it.createdAt)}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-mono truncate max-w-md" title={it.message}>
                                        {it.message}
                                    </td>
                                    <td className="px-4 py-3 text-sm">{it.platform || '-'}</td>
                                    <td className="px-4 py-3 text-sm">{it.appVersion || '-'}</td>
                                    <td className="px-4 py-3 text-sm">
                                        {it.user?.username || it.user?.name || '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        {it.isFatal ? (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">FATAL</span>
                                        ) : (
                                            <span className="text-xs text-gray-500">no</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-4">
                <PaginationBar
                    page={page}
                    pageSize={PAGE_SIZE}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onChange={setPage}
                />
            </div>

            {/* Detail modal */}
            {selected && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
                    <div
                        className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <div>
                                <div className="text-xs text-gray-500">
                                    {tableDateFormate(selected.createdAt)} • {selected.platform} {selected.osVersion} • v{selected.appVersion}
                                </div>
                                <div className="font-semibold text-red-700 mt-1">{selected.message}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-full">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-5 space-y-4">
                            {selected.user && (
                                <div className="text-sm">
                                    <span className="text-gray-500">User: </span>
                                    {selected.user.name} (@{selected.user.username}) — <span className="font-mono text-xs">{selected.user._id}</span>
                                </div>
                            )}
                            <div>
                                <div className="text-xs text-gray-500 uppercase mb-1">Stack</div>
                                <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded-lg overflow-auto">
{selected.stack || '(no stack captured)'}
                                </pre>
                            </div>
                            {selected.componentStack && (
                                <div>
                                    <div className="text-xs text-gray-500 uppercase mb-1">Component Stack</div>
                                    <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded-lg overflow-auto">
{selected.componentStack}
                                    </pre>
                                </div>
                            )}
                            {selected.extra && Object.keys(selected.extra).length > 0 && (
                                <div>
                                    <div className="text-xs text-gray-500 uppercase mb-1">Extra</div>
                                    <pre className="bg-gray-100 text-gray-800 text-xs p-3 rounded-lg overflow-auto">
{JSON.stringify(selected.extra, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
}
